import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isAxiosError } from 'axios';
import './RealtimeConsultPage.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import {
  createRealtimeSession,
  requestRealtimePrescription,
  submitRealtimeIceCandidates,
  submitRealtimeOffer,
} from '../services/realtimeService';
import type {
  HerbPrescription,
  IceCandidatePayload,
  RealtimePrescriptionInsights,
  RealtimeSessionResponse,
  RealtimePrescriptionResponsePayload,
  RealtimeObservationInsight,
} from '../types';

type PeerStatus = RTCPeerConnectionState | 'idle';
type DataChannelStatus = 'closed' | 'connecting' | 'open';

interface VoiceTurn {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  createdAt: Date;
  metadata?: Record<string, unknown> | null;
}

interface RealtimeMessagePayload {
  type: string;
  role?: 'user' | 'assistant';
  text?: string;
  timestamp?: string;
  metadata?: Record<string, unknown> | null;
  conversation_id?: string;
  reason?: string;
  status?: string;
  insight?: RealtimeObservationInsight;
  payload?: RealtimePrescriptionResponsePayload;
}

type AssistantActivity = 'idle' | 'thinking' | 'speaking';
type UserActivity = 'idle' | 'speaking';

const ASSISTANT_ACTIVITY_LABEL: Record<AssistantActivity, string> = {
  idle: '待命',
  thinking: '思考中',
  speaking: '回复中',
};

const USER_ACTIVITY_LABEL: Record<UserActivity, string> = {
  idle: '待命',
  speaking: '讲话中',
};

const isAssistantActivity = (value?: string): value is AssistantActivity =>
  value === 'idle' || value === 'thinking' || value === 'speaking';

const isUserActivity = (value?: string): value is UserActivity =>
  value === 'idle' || value === 'speaking';

const CONNECTION_LABEL: Record<PeerStatus, string> = {
  idle: '待连接',
  new: '新建',
  connecting: '建立中',
  connected: '已连接',
  disconnected: '已断开',
  failed: '失败',
  closed: '已关闭',
};

const CHANNEL_LABEL: Record<DataChannelStatus, string> = {
  closed: '未建立',
  connecting: '握手中',
  open: '已建立',
};

const formatClockTime = (value: Date) =>
  `${value.getHours().toString().padStart(2, '0')}:${value
    .getMinutes()
    .toString()
    .padStart(2, '0')}:${value.getSeconds().toString().padStart(2, '0')}`;

const RealtimeConsultPage: React.FC = () => {
  const [session, setSession] = useState<RealtimeSessionResponse | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [turns, setTurns] = useState<VoiceTurn[]>([]);
  const [connectionState, setConnectionState] = useState<PeerStatus>('idle');
  const [channelState, setChannelState] = useState<DataChannelStatus>('closed');
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);
  const [isNegotiating, setIsNegotiating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [peerId, setPeerId] = useState<string | null>(null);
  const [lastEventType, setLastEventType] = useState<string>('尚未收到事件');
  const [assistantActivity, setAssistantActivity] = useState<AssistantActivity>('idle');
  const [userActivity, setUserActivity] = useState<UserActivity>('idle');
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [cameraAvailable, setCameraAvailable] = useState(true);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [prescription, setPrescription] = useState<HerbPrescription | null>(null);
  const [prescriptionInsights, setPrescriptionInsights] =
    useState<RealtimePrescriptionInsights | null>(null);
  const [isGeneratingPrescription, setIsGeneratingPrescription] = useState(false);
  const [prescriptionError, setPrescriptionError] = useState<string | null>(null);
  const [prescriptionNotes, setPrescriptionNotes] = useState('');
  const [observationInsights, setObservationInsights] =
    useState<RealtimeObservationInsight[]>([]);

  const peerRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const pingIntervalRef = useRef<number | null>(null);
  const pendingIceRef = useRef<IceCandidatePayload[]>([]);
  const flushTimerRef = useRef<number | null>(null);
  const sessionRef = useRef<RealtimeSessionResponse | null>(null);
  const tokenRef = useRef<string | null>(null);
  const pcIdRef = useRef<string | null>(null);
  const negotiatingRef = useRef(false);
  const initOnceRef = useRef(false);

  const ensureLocalMedia = useCallback(async () => {
    if (localStreamRef.current) {
      return localStreamRef.current;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('当前浏览器不支持音视频采集');
    }

    const audioConstraints: MediaTrackConstraints = {
      echoCancellation: true,
      noiseSuppression: true,
      channelCount: 1,
    };
    const videoConstraints: MediaTrackConstraints = {
      width: { ideal: 960 },
      height: { ideal: 720 },
      facingMode: 'user',
    };

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
        video: videoConstraints,
      });
      setCameraError(null);
    } catch (captureError) {
      console.warn('摄像头不可用，降级为语音通话', captureError);
      setCameraError('摄像头不可用，本次会话将仅传输语音。');
      setCameraAvailable(false);
      stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
      });
    }

    localStreamRef.current = stream;
    setPermissionGranted(true);

    const hasVideoTrack = stream.getVideoTracks().length > 0;
    setCameraAvailable(hasVideoTrack);
    setCameraEnabled(hasVideoTrack);

    if (localVideoRef.current) {
      localVideoRef.current.srcObject = stream;
      localVideoRef.current.muted = true;
      const playPromise = localVideoRef.current.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(() => undefined);
      }
    }

    return stream;
  }, []);

  const cleanupPeerConnection = useCallback(() => {
    if (flushTimerRef.current !== null) {
      window.clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    pendingIceRef.current = [];
    if (pingIntervalRef.current !== null) {
      window.clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (dataChannelRef.current) {
      try {
        dataChannelRef.current.close();
      } catch (closeError) {
        console.warn('关闭数据通道时出现异常', closeError);
      }
    }
    dataChannelRef.current = null;
    if (peerRef.current) {
      peerRef.current.ontrack = null;
      peerRef.current.onicecandidate = null;
      peerRef.current.onconnectionstatechange = null;
      peerRef.current.oniceconnectionstatechange = null;
      peerRef.current.ondatachannel = null;
      try {
        peerRef.current.close();
      } catch (closeError) {
        console.warn('关闭 WebRTC 连接时出现异常', closeError);
      }
    }
    peerRef.current = null;
    if (remoteStreamRef.current) {
      remoteStreamRef.current.getTracks().forEach((track) => track.stop());
      remoteStreamRef.current = null;
    }
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
    pcIdRef.current = null;
    setPeerId(null);
    setChannelState('closed');
    setConnectionState('disconnected');
    setAutoplayBlocked(false);
  }, []);

  const sendIceCandidates = useCallback(async () => {
    if (!sessionRef.current || !tokenRef.current || !pcIdRef.current) {
      return;
    }
    if (!pendingIceRef.current.length) {
      return;
    }
    const batch = pendingIceRef.current.splice(0, pendingIceRef.current.length);
    try {
      await submitRealtimeIceCandidates({
        token: tokenRef.current,
        pcId: pcIdRef.current,
        candidates: batch,
        icePath: sessionRef.current.transport.ice_path,
      });
    } catch (iceError) {
      console.warn('发送 ICE 候选失败', iceError);
    }
  }, []);

  const scheduleIceFlush = useCallback(() => {
    if (flushTimerRef.current !== null) {
      return;
    }
    flushTimerRef.current = window.setTimeout(async () => {
      flushTimerRef.current = null;
      await sendIceCandidates();
      if (pendingIceRef.current.length) {
        scheduleIceFlush();
      }
    }, 200);
  }, [sendIceCandidates]);

  const handleTransportMessage = useCallback(
    (raw: string) => {
      const processPayload = (payload: RealtimeMessagePayload) => {
        if (!payload || typeof payload.type !== 'string') {
          setLastEventType('未知事件');
          return;
        }
        setLastEventType(payload.type);
        if (payload.type === 'session_ready') {
          if (payload.conversation_id) {
            setConversationId(payload.conversation_id);
          }
          setError(null);
          setAssistantActivity('idle');
          setUserActivity('idle');
          setAutoplayBlocked(false);
          return;
        }
        if (payload.type === 'observation_insight' && payload.insight) {
          const insight = payload.insight as RealtimeObservationInsight;
          setObservationInsights((prev) => {
            const deduped = prev.filter((item) => item.timestamp !== insight.timestamp);
            return [insight, ...deduped].slice(0, 6);
          });
          return;
        }
        if (payload.type === 'auto_prescription_ready' && payload.payload) {
          const autoPayload = payload.payload as RealtimePrescriptionResponsePayload;
          setPrescription(autoPayload.prescription);
          setPrescriptionInsights(autoPayload.insights ?? null);
          setPrescriptionNotes('');
          setPrescriptionError(null);
          setIsGeneratingPrescription(false);
          return;
        }
        if (payload.type === 'stt_warning') {
          const warningText = payload.text || '未检测到语音输入，请检查麦克风。';
          setError(warningText);
          const createdAt = payload.timestamp ? new Date(payload.timestamp) : new Date();
          setTurns((prev) => [
            ...prev,
            {
              id: `assistant-warning-${Date.now()}`,
              role: 'assistant',
              text: warningText,
              createdAt,
              metadata: payload.reason ? { reason: payload.reason } : null,
            },
          ]);
          return;
        }
        if (payload.type === 'error') {
          setError(payload.text || '实时语音链路发生未知错误');
          return;
        }
        if (payload.type === 'activity' && payload.role) {
          const status = payload.status ?? 'idle';
          if (payload.role === 'assistant') {
            setAssistantActivity(isAssistantActivity(status) ? status : 'idle');
          } else if (payload.role === 'user') {
            setUserActivity(isUserActivity(status) ? status : 'idle');
          }
          return;
        }
        const createdAt = payload.timestamp ? new Date(payload.timestamp) : new Date();
        if (payload.conversation_id) {
          setConversationId(payload.conversation_id);
        }
        if (payload.type === 'transcript' && payload.role === 'user') {
          setTurns((prev) => [
            ...prev,
            {
              id: `user-${Date.now()}`,
              role: 'user',
              text: payload.text || '',
              createdAt,
            },
          ]);
          return;
        }
        if (payload.type === 'assistant_message') {
          setTurns((prev) => [
            ...prev,
            {
              id: `assistant-${Date.now()}`,
              role: 'assistant',
              text: payload.text || '',
              createdAt,
              metadata: payload.metadata ?? null,
            },
          ]);
          return;
        }
        if (payload.type === 'transcript' && payload.role === 'assistant') {
          setTurns((prev) => [
            ...prev,
            {
              id: `assistant-transcript-${Date.now()}`,
              role: 'assistant',
              text: payload.text || '',
              createdAt,
              metadata: payload.metadata ?? null,
            },
          ]);
        }
      };

      const attemptParse = (chunk: string) => {
        const normalized = chunk.trim();
        if (!normalized) {
          return;
        }

        if (normalized.startsWith('event:')) {
          const eventName = normalized.slice('event:'.length).trim();
          if (eventName) {
            setLastEventType(eventName);
          }
          return;
        }

        let content = normalized;
        if (content.startsWith('data:')) {
          content = content.slice('data:'.length).trim();
        }

        if (!content) {
          return;
        }

        try {
          const payload = JSON.parse(content) as RealtimeMessagePayload;
          processPayload(payload);
        } catch (parseError) {
          console.warn('解析实时通道消息失败', content, parseError);
          setLastEventType('解析失败');
        }
      };

      const trimmed = raw.trim();
      if (!trimmed) {
        return;
      }

      if (trimmed.startsWith('{')) {
        attemptParse(trimmed);
        return;
      }

      const segments = trimmed.split('\n');

      if (!segments.length) {
        return;
      }

      segments.forEach(attemptParse);
    },
    [],
  );

  const dispatchTransportData = useCallback(
    (data: string | ArrayBuffer | Blob) => {
      if (typeof data === 'string') {
        handleTransportMessage(data);
        return;
      }
      if (data instanceof ArrayBuffer) {
        const decoded = new TextDecoder('utf-8').decode(data);
        handleTransportMessage(decoded);
        return;
      }
      if (data instanceof Blob) {
        data
          .text()
          .then((text) => handleTransportMessage(text))
          .catch((blobError) => {
            console.warn('解析 Blob 类型实时消息失败', blobError);
            setLastEventType('解析失败');
          });
      }
    },
    [handleTransportMessage],
  );

  const attemptResumeAudio = useCallback(async () => {
    if (!remoteAudioRef.current) {
      return;
    }
    if (!autoPlay) {
      remoteAudioRef.current.muted = true;
      return;
    }
    try {
      remoteAudioRef.current.muted = false;
      await remoteAudioRef.current.play();
      setAutoplayBlocked(false);
    } catch (playError) {
      console.warn('恢复语音播放失败', playError);
      setAutoplayBlocked(true);
    }
  }, [autoPlay]);

  const setupPeerConnection = useCallback(
    async (activeSession: RealtimeSessionResponse) => {
      const stream = await ensureLocalMedia();
      await cleanupPeerConnection();
      setConnectionState('connecting');
      setChannelState('connecting');
      sessionRef.current = activeSession;
      tokenRef.current = activeSession.signaling_token;
      pendingIceRef.current = [];

      const rtcConfig: RTCConfiguration = {};
      if (activeSession.transport.ice_servers?.length) {
        rtcConfig.iceServers = activeSession.transport.ice_servers.map((server) => ({
          urls: server.urls,
          username: server.username,
          credential: server.credential,
        }));
      }

      const pc = new RTCPeerConnection(rtcConfig);
      peerRef.current = pc;

      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const remoteStream = new MediaStream();
      remoteStreamRef.current = remoteStream;
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = remoteStream;
        remoteAudioRef.current.muted = !autoPlay;
      }

      pc.ontrack = (event) => {
        event.streams.forEach((incoming) => {
          incoming.getTracks().forEach((incomingTrack) => remoteStream.addTrack(incomingTrack));
        });
        if (remoteAudioRef.current) {
          if (autoPlay) {
            void attemptResumeAudio();
          } else {
            remoteAudioRef.current.muted = true;
          }
        }
      };

      pc.onconnectionstatechange = () => {
        const state = pc.connectionState;
        setConnectionState(state);
        if (state === 'failed') {
          setError('WebRTC 连接失败，请稍后重试。');
        }
        if (state === 'connected') {
          setError(null);
        }
      };

      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === 'disconnected') {
          setConnectionState('disconnected');
        }
      };

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          pendingIceRef.current.push({
            candidate: event.candidate.candidate,
            sdp_mid: event.candidate.sdpMid ?? '',
            sdp_mline_index: event.candidate.sdpMLineIndex ?? 0,
          });
          scheduleIceFlush();
        } else {
          void sendIceCandidates();
        }
      };

      pc.ondatachannel = (event) => {
        event.channel.onmessage = (messageEvent) =>
          dispatchTransportData(messageEvent.data as string | ArrayBuffer | Blob);
      };

      const dataChannel = pc.createDataChannel('app');
      dataChannelRef.current = dataChannel;
      dataChannel.onopen = () => {
        setChannelState('open');
        if (pingIntervalRef.current !== null) {
          window.clearInterval(pingIntervalRef.current);
        }
        const sendPing = () => {
          if (dataChannel.readyState === 'open') {
            try {
              dataChannel.send(`ping:${Date.now()}`);
            } catch (pingError) {
              console.warn('发送心跳失败', pingError);
            }
          }
        };
        sendPing();
        pingIntervalRef.current = window.setInterval(sendPing, 2000);
      };
      dataChannel.onclose = () => {
        setChannelState('closed');
        if (pingIntervalRef.current !== null) {
          window.clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
      };
      dataChannel.onerror = (channelError) => {
        console.error('实时数据通道错误', channelError);
        setChannelState('closed');
      };
      dataChannel.onmessage = (event) =>
        dispatchTransportData(event.data as string | ArrayBuffer | Blob);

      try {
        const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
        await pc.setLocalDescription(offer);
        const answer = await submitRealtimeOffer({
          token: activeSession.signaling_token,
          sdp: offer.sdp ?? '',
          type: offer.type,
          pcId: pcIdRef.current,
          restartPc: false,
          signalingPath: activeSession.transport.signaling_path,
        });
        pcIdRef.current = answer.pc_id;
        setPeerId(answer.pc_id);
        await pc.setRemoteDescription({ type: answer.type as RTCSdpType, sdp: answer.sdp });
        await sendIceCandidates();
        setConnectionState(pc.connectionState || 'connecting');
      } catch (negotiationError) {
        console.error('建立 WebRTC 会话失败', negotiationError);
        setError('建立实时通话失败，请稍后再试。');
        throw negotiationError;
      }
    },
    [
      autoPlay,
      cleanupPeerConnection,
      ensureLocalMedia,
      dispatchTransportData,
      scheduleIceFlush,
      sendIceCandidates,
      attemptResumeAudio,
    ],
  );

  const initializeSession = useCallback(async () => {
    if (negotiatingRef.current) {
      return;
    }
    negotiatingRef.current = true;
    setIsNegotiating(true);
    setError(null);
    setAssistantActivity('idle');
    setUserActivity('idle');
    try {
      const nextSession = await createRealtimeSession({
        metadata: {
          source: 'web_spa',
          mode: 'realtime_webrtc',
          auto_play: autoPlay,
        },
      });
      setSession(nextSession);
      setConversationId(nextSession.conversation_id);
      setTurns([]);
      setPrescription(null);
      setPrescriptionInsights(null);
      setPrescriptionError(null);
  setPrescriptionNotes('');
  setObservationInsights([]);
      await setupPeerConnection(nextSession);
      setConnectionState('connecting');
    } catch (initError) {
      console.error('初始化实时会话失败', initError);
      setError('实时问诊服务暂时不可用，请稍后重试。');
      cleanupPeerConnection();
    } finally {
      negotiatingRef.current = false;
      setIsNegotiating(false);
    }
  }, [autoPlay, cleanupPeerConnection, setupPeerConnection]);

  const toggleCamera = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) {
      void ensureLocalMedia().catch(() => undefined);
      return;
    }
    const tracks = stream.getVideoTracks();
    if (!tracks.length) {
      setCameraAvailable(false);
      setCameraEnabled(false);
      setCameraError('当前无可用摄像头，无法开启画面。');
      return;
    }
    const nextEnabled = !cameraEnabled;
    tracks.forEach((track) => {
      track.enabled = nextEnabled;
    });
    setCameraEnabled(nextEnabled);
  }, [cameraEnabled, ensureLocalMedia]);

  const disconnectSession = useCallback(() => {
    negotiatingRef.current = false;
    setIsNegotiating(false);
    cleanupPeerConnection();
    sessionRef.current = null;
    tokenRef.current = null;
    setConnectionState('disconnected');
    setAssistantActivity('idle');
    setUserActivity('idle');
    setAutoplayBlocked(false);
    setObservationInsights([]);
  }, [cleanupPeerConnection]);

  const handleGeneratePrescription = useCallback(async () => {
    const activeSession = sessionRef.current ?? session;
    if (!activeSession) {
      setPrescriptionError('当前暂无活跃会话，无法生成处方。');
      return;
    }

    setIsGeneratingPrescription(true);
    setPrescriptionError(null);

    try {
      const response = await requestRealtimePrescription(activeSession.session_id, {
        notes: prescriptionNotes.trim() || undefined,
      });
      setPrescription(response.prescription);
      setPrescriptionInsights(response.insights ?? null);
      setPrescriptionNotes('');
    } catch (requestError) {
      if (isAxiosError(requestError) && requestError.response) {
        const detail = requestError.response.data?.detail;
        if (typeof detail === 'string') {
          setPrescriptionError(detail);
        } else if (detail?.message) {
          let message = detail.message as string;
          const validationMessages = detail.validation?.messages;
          if (Array.isArray(validationMessages) && validationMessages.length > 0) {
            const first = validationMessages.find((item: { passed?: boolean }) => item.passed === false)
              ?? validationMessages[0];
            if (first?.message) {
              message = `${message}：${first.message}`;
            }
          }
          setPrescriptionError(message);
        } else {
          setPrescriptionError('生成处方失败，请稍后重试。');
        }
      } else {
        setPrescriptionError('生成处方失败，请稍后重试。');
      }
    } finally {
      setIsGeneratingPrescription(false);
    }
  }, [prescriptionNotes, session]);

  useEffect(() => {
    ensureLocalMedia().catch((mediaError) => {
      console.error('音视频初始化失败', mediaError);
      setError('无法访问麦克风或摄像头，请检查浏览器权限。');
    });
  }, [ensureLocalMedia]);

  useEffect(() => {
    if (!initOnceRef.current) {
      initOnceRef.current = true;
      initializeSession();
    }
    return () => {
      initOnceRef.current = false;
      disconnectSession();
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
      }
    };
  }, [disconnectSession, initializeSession]);

  useEffect(() => {
    if (!remoteAudioRef.current) {
      return;
    }
    remoteAudioRef.current.muted = !autoPlay;
    if (autoPlay) {
      void attemptResumeAudio();
    } else {
      remoteAudioRef.current.pause();
      setAutoplayBlocked(false);
    }
  }, [autoPlay, attemptResumeAudio]);

  useEffect(() => {
    if (autoPlay && assistantActivity === 'speaking') {
      void attemptResumeAudio();
    }
  }, [assistantActivity, autoPlay, attemptResumeAudio]);

  useEffect(() => {
    if (!localVideoRef.current || !localStreamRef.current) {
      return;
    }
    if (localVideoRef.current.srcObject !== localStreamRef.current) {
      localVideoRef.current.srcObject = localStreamRef.current;
    }
  }, [cameraAvailable, cameraEnabled]);

  const sessionInfo = useMemo(() => {
    if (!session) {
      return null;
    }
    return {
      stt: session.stt_provider,
      tts: session.tts_provider,
      transport: session.transport?.transport,
      expiry: new Date(session.expires_at).toLocaleString(),
      vad: session.transport?.vad,
      voice: session.transport?.tts_voice_id,
      video: session.video_provider ?? null,
    };
  }, [session]);

  const latestObservation = useMemo(
    () => (observationInsights.length > 0 ? observationInsights[0] : null),
    [observationInsights],
  );

  const assistantGuidance = useMemo(() => {
    switch (assistantActivity) {
      case 'speaking':
        return 'AI 正在播报语音，请等待播报结束再开口。';
      case 'thinking':
        return 'AI 正在整理答复，即将开始回复。';
      default:
        return 'AI 待命，可继续描述症状。';
    }
  }, [assistantActivity]);

  const userGuidance = useMemo(() => {
    switch (userActivity) {
      case 'speaking':
        return '系统正在收音，请保持自然语速。';
      default:
        return '可随时开口提问。';
    }
  }, [userActivity]);

  const markdownRemarkPlugins = useMemo(() => [remarkGfm], []);
  const markdownRehypePlugins = useMemo(() => [rehypeSanitize], []);

  return (
    <div className="rtc-layout">
      <main className="rtc-main">
        <header className="rtc-topbar">
          <div className="rtc-heading">
            <h2 className="rtc-title">实时语音问诊</h2>
            <p className="rtc-subtitle">
              通过 WebRTC 低延迟链路与 AI 中医助手连线，实时描述症状、聆听即时答复。
            </p>
          </div>
          <div className="rtc-tags">
            <span className="rtc-tag">会话：{conversationId ? conversationId.slice(0, 12) : '等待连接'}</span>
            <span className="rtc-tag">Peer：{peerId ? peerId.slice(0, 12) : '未分配'}</span>
          </div>
        </header>

        {error && (
          <div className="rtc-alert rtc-alert-error" role="alert">
            <span>{error}</span>
          </div>
        )}

        {autoplayBlocked && (
          <div className="rtc-alert rtc-alert-warning" role="alert">
            <span>浏览器阻止了语音自动播放，请点击继续收听。</span>
            <button type="button" onClick={() => void attemptResumeAudio()}>
              恢复语音
            </button>
          </div>
        )}

        {cameraError && (
          <div className="rtc-alert rtc-alert-warning" role="status">
            <span>{cameraError}</span>
          </div>
        )}

        <div className="rtc-streams" aria-label="实时视频">
          <div className="rtc-video-tile">
            <div className={`rtc-video-frame${!cameraEnabled ? ' rtc-video-frame--muted' : ''}`}>
              <video ref={localVideoRef} className="rtc-video-element" autoPlay playsInline muted />
              {!cameraAvailable && <div className="rtc-video-placeholder">摄像头不可用</div>}
              {cameraAvailable && !cameraEnabled && (
                <div className="rtc-video-placeholder">摄像头已关闭</div>
              )}
            </div>
            <span className="rtc-video-caption">我</span>
          </div>
        </div>

        <section className="rtc-observation" aria-live="polite">
          <div className="rtc-observation-head">
            <div>
              <h3>望诊速记</h3>
              <p>摄像头画面将自动提炼面色、光泽与津液提示，供问诊参考。</p>
            </div>
            {latestObservation && (
              <span className="rtc-observation-badge">
                {new Date(latestObservation.timestamp).toLocaleTimeString()}
              </span>
            )}
          </div>
          {latestObservation ? (
            <div className="rtc-observation-body">
              <p className="rtc-observation-summary">{latestObservation.summary}</p>
              {latestObservation.highlights.length > 1 && (
                <ul className="rtc-observation-highlights">
                  {latestObservation.highlights.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
              <div className="rtc-observation-metrics">
                <div>
                  <span className="rtc-observation-label">亮度</span>
                  <strong>{Math.round(latestObservation.metrics.brightness)}</strong>
                </div>
                <div>
                  <span className="rtc-observation-label">温阳指数</span>
                  <strong>{latestObservation.metrics.warmth_index.toFixed(1)}</strong>
                </div>
                <div>
                  <span className="rtc-observation-label">津液指数</span>
                  <strong>{latestObservation.metrics.dryness_index.toFixed(2)}</strong>
                </div>
                <div>
                  <span className="rtc-observation-label">清晰度</span>
                  <strong>{latestObservation.metrics.clarity_score.toFixed(1)}</strong>
                </div>
              </div>
              <footer className="rtc-observation-foot">
                <span>{latestObservation.face_detected ? '已检测到面部主体' : '未稳定检测到面部主体'}</span>
                <span>
                  画质评估：
                  {latestObservation.quality.quality_label === 'excellent'
                    ? '优秀'
                    : latestObservation.quality.quality_label === 'acceptable'
                      ? '可用'
                      : '建议重新调整'}
                </span>
              </footer>
            </div>
          ) : (
            <div className="rtc-observation-placeholder">
              摄像头开启后，系统会自动记录面色与舌象提示，帮助加速望诊。
            </div>
          )}
        </section>

        <div className="rtc-chat" role="log" aria-live="polite">
          {turns.length === 0 ? (
            <div className="rtc-placeholder">
              会话就绪后即可开始描述症状，例如：“最近咽喉干痒、夜间咳嗽，应如何调理？”
            </div>
          ) : (
            <ul className="rtc-message-list">
              {turns.map((turn) => (
                <li key={turn.id} className={`rtc-message rtc-${turn.role}`}>
                  <div className="rtc-message-meta">
                    <span className="rtc-message-role">
                      {turn.role === 'user' ? '我' : 'AI 中医助手'}
                    </span>
                    <time className="rtc-message-time">{formatClockTime(turn.createdAt)}</time>
                  </div>
                  <p className="rtc-message-body">{turn.text || '（无文字内容）'}</p>
                  {turn.metadata && turn.role === 'assistant' && (
                    <details className="rtc-metadata">
                      <summary>解析提示</summary>
                      <pre>{JSON.stringify(turn.metadata, null, 2)}</pre>
                    </details>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <section className="rtc-prescription" aria-live="polite">
          <div className="rtc-prescription-head">
            <div>
              <h3>AI 处方草案</h3>
              <p>基于实时问诊记录生成，仅供专业医师参考与调整。</p>
            </div>
            <div className="rtc-prescription-actions">
              <button
                type="button"
                className="rtc-button rtc-button-primary"
                onClick={handleGeneratePrescription}
                disabled={isGeneratingPrescription}
              >
                {isGeneratingPrescription ? '生成中…' : '生成处方'}
              </button>
            </div>
          </div>
          <label htmlFor="rtc-prescription-notes" className="rtc-prescription-label">
            补充说明（可选）
          </label>
          <textarea
            id="rtc-prescription-notes"
            className="rtc-prescription-notes"
            rows={3}
            placeholder="例如：患者体质偏虚寒、近期服药、过敏史等"
            value={prescriptionNotes}
            onChange={(event) => setPrescriptionNotes(event.target.value)}
          />
          {prescriptionError && <p className="rtc-prescription-error">{prescriptionError}</p>}
          {isGeneratingPrescription && !prescription && (
            <p className="rtc-prescription-hint">正在召集中医药材建议，请稍候…</p>
          )}
          {prescription && (
            <div className="rtc-prescription-body">
              <div className="rtc-prescription-summary">
                <h4>{prescription.title || 'AI 推荐处方'}</h4>
                <div className="rtc-prescription-meta">
                  {prescriptionInsights?.diagnosis && (
                    <span>辨证：{prescriptionInsights.diagnosis}</span>
                  )}
                  {prescriptionInsights?.symptoms && (
                    <span>症状：{prescriptionInsights.symptoms}</span>
                  )}
                  {prescriptionInsights?.notes && <span>叮嘱：{prescriptionInsights.notes}</span>}
                </div>
                <div className="rtc-prescription-herbs">
                  {prescription.herbs.map((herb) => (
                    <div key={`${herb.name}-${herb.dosage}`} className="rtc-prescription-herb">
                      <strong>{herb.name}</strong>
                      <span>{herb.dosage || '剂量待定'}</span>
                      {herb.reason && <p>{herb.reason}</p>}
                    </div>
                  ))}
                </div>
                {prescription.validation && (
                  <ul className={`rtc-prescription-validation status-${prescription.validation.status}`}>
                    {prescription.validation.messages.map((message) => (
                      <li key={message.rule_id}>
                        <span className="rtc-validation-title">{message.message}</span>
                        {message.description && (
                          <span className="rtc-validation-desc">{message.description}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rtc-prescription-markdown">
                <ReactMarkdown
                  remarkPlugins={markdownRemarkPlugins}
                  rehypePlugins={markdownRehypePlugins}
                >
                  {prescription.prescription_text || '暂无详细处方文本'}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </section>
      </main>

      <aside className="rtc-sidebar">
        <section className="rtc-card">
          <h3 className="rtc-card-title">链路状态</h3>
          <dl className="rtc-stats">
            <div className="rtc-stat">
              <dt>WebRTC</dt>
              <dd>
                <span className={`rtc-pill state-${connectionState}`}>
                  {CONNECTION_LABEL[connectionState]}
                </span>
              </dd>
            </div>
            <div className="rtc-stat">
              <dt>数据通道</dt>
              <dd>
                <span className={`rtc-pill channel-${channelState}`}>
                  {CHANNEL_LABEL[channelState]}
                </span>
              </dd>
            </div>
            <div className="rtc-stat">
              <dt>传输模式</dt>
              <dd>{sessionInfo?.transport ?? 'webrtc'}</dd>
            </div>
            <div className="rtc-stat">
              <dt>语音识别</dt>
              <dd>{sessionInfo?.stt ?? '未知'}</dd>
            </div>
            <div className="rtc-stat">
              <dt>语音合成</dt>
              <dd>
                {sessionInfo?.tts ?? '未知'}
                {sessionInfo?.voice ? `（${sessionInfo.voice}）` : ''}
              </dd>
            </div>
            <div className="rtc-stat">
              <dt>视频通道</dt>
              <dd>{cameraAvailable ? (cameraEnabled ? '开启' : '已关闭') : '不可用'}</dd>
            </div>
            {sessionInfo?.video && (
              <div className="rtc-stat">
                <dt>视频提供方</dt>
                <dd>{sessionInfo.video}</dd>
              </div>
            )}
            <div className="rtc-stat">
              <dt>有效期</dt>
              <dd>{sessionInfo?.expiry ?? '---'}</dd>
            </div>
            <div className="rtc-stat">
              <dt>最近事件</dt>
              <dd className="rtc-stat-muted">{lastEventType}</dd>
            </div>
          </dl>
        </section>

        <section className="rtc-card">
          <h3 className="rtc-card-title">实时动态</h3>
          <div className="rtc-activity">
            <div className="rtc-activity-block">
              <span className={`rtc-chip assistant ${assistantActivity}`}>
                {ASSISTANT_ACTIVITY_LABEL[assistantActivity]}
              </span>
              <p className="rtc-activity-note">{assistantGuidance}</p>
            </div>
            <div className="rtc-activity-block">
              <span className={`rtc-chip user ${userActivity}`}>
                {USER_ACTIVITY_LABEL[userActivity]}
              </span>
              <p className="rtc-activity-note">{userGuidance}</p>
            </div>
          </div>
        </section>

        <section className="rtc-card">
          <h3 className="rtc-card-title">控制台</h3>
          <div className="rtc-actions">
            <button
              type="button"
              className="rtc-button rtc-button-primary"
              onClick={initializeSession}
              disabled={isNegotiating}
            >
              {isNegotiating ? '建立中…' : '重新建立会话'}
            </button>
            <button
              type="button"
              className="rtc-button rtc-button-secondary"
              onClick={disconnectSession}
            >
              断开连接
            </button>
            <button
              type="button"
              className="rtc-button rtc-button-secondary"
              onClick={toggleCamera}
              disabled={!cameraAvailable && cameraError !== null}
            >
              {cameraAvailable ? (cameraEnabled ? '关闭摄像头' : '开启摄像头') : '摄像头不可用'}
            </button>
            <button
              type="button"
              className="rtc-button rtc-button-tertiary"
              onClick={() => {
                setTurns([]);
                setError(null);
              }}
            >
              清空历史
            </button>
          </div>
          <label htmlFor="auto-play-toggle" className="rtc-toggle">
            <input
              id="auto-play-toggle"
              type="checkbox"
              checked={autoPlay}
              onChange={(event) => setAutoPlay(event.target.checked)}
            />
            自动播放 AI 语音答复
          </label>
          {sessionInfo?.vad && (
            <p className="rtc-hint">
              VAD：阈值 {sessionInfo.vad.confidence.toFixed(2)} · 起始 {sessionInfo.vad.start_secs}s · 结束 {sessionInfo.vad.stop_secs}s
            </p>
          )}
          {!permissionGranted && (
            <p className="rtc-hint rtc-hint-warning">⚠️ 麦克风权限尚未开启，请检查浏览器设置。</p>
          )}
          <p className="rtc-hint">
            摄像头状态：
            {cameraAvailable ? (cameraEnabled ? '已开启' : '已关闭') : '不可用'}
          </p>
        </section>
      </aside>

      <audio ref={remoteAudioRef} autoPlay playsInline hidden />
    </div>
  );
};

export default RealtimeConsultPage;
